






































































import Vue from "vue";
import { Location } from "vue-router";
import IconDict from "@joyz/app-core/assets/icons/default/Dictionary.svg";
import { DictationTile } from "@joyz/app-core/models/control";
import {
  ROUTES,
  EXERCISE_TYPE,
  DICTATION_EVENT,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_GETTER_NAMES,
  VUEX_ROOT_ACTION_NAMES
} from "@tt-app/constants";
import { ExerciseViewData, ExerciseViewMethods } from "@tt-app/models/exercise";
import {
  chatHandler,
  dictationProblemsHandler,
  exerciseHandler,
  exerciseNavHandler,
  FlatExerciseListDetails,
  getNewExerciseViewState,
  nextExerciseRouter
} from "@tt-app/utils";
import {
  ChatState,
  ApiExerciseBtnAction,
  ChatAllObjects,
  ExerciseFinalScore,
  ApiChatMessage,
  ApiChatImage
} from "../../models/exercise/common";
import {
  DictationProblemsState,
  DictationEvent,
  DictationEventType
} from "../../models/exercise/dictation";
import { AppChatDictationUserSpeaker } from "../../models/exercise/dictation/chat";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import ChatBody from "@tt-app/components/exercise/ChatBody.vue";
import ChatTrophy from "@tt-app/components/exercise/ChatTrophy.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import {
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { audioContext } from "@tt-app/utils/audio/audioContext";

const { COURSE } = ROUTES;
const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

interface Props {
  iconDict: string;
}

interface Data extends ExerciseViewData {
  audioDecoderPlayer: AudioDecoderPlayer;
  chat: ChatState;
  dictationProblems: DictationProblemsState;
  dictationEvent: DictationEvent[];
}

interface Methods extends ExerciseViewMethods {
  fetchAssignmentDetails: () => void;
  fetchCourseDetails: () => void;
  initChat: () => Promise<void>;
  showNextChatMessage: () => void;
  onActionBtnClick: (action: ApiExerciseBtnAction) => void;
  onDictionaryClick: () => void;
  onDictationSubmit: (answer: DictationTile[]) => void;
  onDictationAudioReplay: (item: ApiChatMessage | AppChatDictationUserSpeaker) => void;
  onDictationTileSelect: (tile: DictationTile) => void;
  onDictationTileUnselect: (tile: DictationTile) => void;
  onImageOpened: (item: ApiChatImage) => void;
  onImageClosed: (item: ApiChatImage) => void;
  onNewDictationEvent: (
    type: DictationEventType,
    args: { tokenId?: number; objectUuid?: string }
  ) => void;
  refreshAudioPlayer: () => void;
  updateFooter: () => void;
}

interface Computed {
  assignmentId?: string;
  assignmentsCompleted: number;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  exerciseUuid: string;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  flatExerciseList: FlatExerciseListDetails[];
  limitedModeActive: boolean;
  nextExercise?: FlatExerciseListDetails;
  nextExerciseButtonText: string;
  session: Session;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    AssignmentCompletionModal,
    ExerciseHeaderTitle,
    ChatBody,
    ChatTrophy,
    LayoutExercise
  },

  props: {
    iconDict: { type: String, default: IconDict }
  },

  data() {
    return {
      ...getNewExerciseViewState(EXERCISE_TYPE.DICTATION, {
        name: COURSE.DETAILS.NAME,
        params: { courseUuid: this.$route.params.courseUuid }
      }),
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      chat: chatHandler.getNewState(),
      dictationProblems: dictationProblemsHandler.getNewState(),
      dictationEvent: []
    };
  },

  watch: {
    "nav.requireExitConfirmation": {
      handler(newValue) {
        window.onbeforeunload = null;

        if (newValue) {
          window.onbeforeunload = () => {
            return true;
          };
        }
      }
    },
    "chat.display.lastItem": {
      async handler(lastItem: ChatAllObjects, oldItem: ChatAllObjects) {
        if (oldItem === lastItem) {
          return;
        }

        // Ensure that footer buttons are visible
        if (
          lastItem.type === "SHOW_START_BUTTONS" ||
          lastItem.type === "SHOW_END_BUTTONS" ||
          lastItem.type === "SHOW_REVIEW"
        ) {
          this.updateFooter();
        }
        // Showing trophy does not display anything and requests API call
        else if (lastItem.type === "SHOW_TROPHY") {
          const finalScore: ExerciseFinalScore = {
            correctCount: this.dictationProblems.results.filter(r => r.retryCount === 0).length,
            incorrectCount: this.dictationProblems.results.filter(r => r.retryCount > 0).length
          };
          this.exercise = await exerciseHandler.endExercise(this, this.exercise, finalScore);

          this.nav.requireExitConfirmation = false;

          this.exercise.footer = {
            buttons: [{ text: this.$__("ex.layout.review.btn"), action: "REVIEW" }],
            disabled: false,
            show: true
          };
        }
      },
      immediate: false,
      deep: false
    }
  },

  async created() {
    this.fetchCourseDetails();
    await this.initChat();

    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeDestroy() {
    this.audioDecoderPlayer.destroy();

    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeRouteEnter(to, from, next) {
    next((vm: Vue) => {
      vm.$data.exercise = exerciseHandler.checkAssignmentId(vm.$data.exercise, from);
    });
  },

  // https://router.vuejs.org/guide/advanced/navigation-guards.html#in-component-guards
  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  computed: {
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    assignmentsCompleted() {
      const completedCount = this.exercise?.results?.trophyInfo?.numAssignmentsSubmitted;
      return completedCount || 0;
    },
    courseUuid() {
      return this.$route.params.courseUuid;
    },
    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },
    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },
    feedbackRouteParams() {
      const exerciseStarted = this.exercise.logId > 0;
      const exerciseFinished = this.exercise.status === "finished";

      return {
        withFeedback: Boolean(exerciseStarted && !exerciseFinished).toString(),
        logId: this.exercise.logId.toString()
      };
    },
    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    limitedModeActive() {
      return Boolean(this.session?.limitedMode?.id);
    },
    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },
    nextExerciseButtonText() {
      let i18nString = "exercise.end";

      if (this.nextExercise) {
        i18nString = "exercise.next";
      }

      return this.$__(i18nString);
    },
    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    }
  },

  methods: {
    exit() {
      exerciseNavHandler.exit(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },

    fetchCourseDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    onExitCancelled() {
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async initChat() {
      // (Re-)init states
      let fallback: Location;
      if (this.assignmentId) {
        await this.fetchAssignmentDetails();
      }

      if (this.$route.params.assignmentId && this.$store.state.assignment.details) {
        fallback = {
          name: ROUTES.ASSIGNMENT.DETAILS.NAME,
          params: {
            assignmentId: this.$route.params.assignmentId
          }
        };
      } else {
        fallback = {
          name: ROUTES.COURSE.DETAILS.NAME,
          params: {
            courseUuid: this.$route.params.courseUuid
          }
        };
      }

      const newState = getNewExerciseViewState(EXERCISE_TYPE.DICTATION, fallback);
      this.exercise = newState.exercise;
      this.nav = newState.nav;

      this.chat = chatHandler.getNewState();
      this.dictationProblems = dictationProblemsHandler.getNewState();

      // Load chat
      this.exercise.status = "loading";
      try {
        this.chat = await chatHandler.loadData(this, this.chat, this.exercise);
      } catch (err) {
        this.loadingError = this.$__("chat.error.loading");
        this.exercise.status = "error";
        return;
      }
      if (Number(this.assignmentId)) {
        this.exercise.assignmentId = Number(this.assignmentId);
      }
      this.exercise.status = "loaded";

      // Update states after data loading
      this.exercise = exerciseHandler.loadHeaderFromChat(this.exercise, this.chat);

      this.exercise = await exerciseHandler.startExercise(this, this.exercise);

      // Start the chat
      this.showNextChatMessage();
      this.exercise.status = "in-progress";
    },

    async onActionBtnClick(action) {
      // Start action
      if (action === "START") {
        // Immediately hide the footer buttons to avoid rapid-fire clicking. This
        // must be handled in the view, not in the handlers, for immediate reactivity
        this.exercise.footer.buttons = [];

        this.nav.requireExitConfirmation = true;

        this.showNextChatMessage();
      }
      // // Show review page
      else if (action === "REVIEW") {
        this.showNextChatMessage();

        this.exercise.status = "finished";
        this.chat.allowUserInteraction = true;
        this.nav.requireExitConfirmation = false;
      }
      // Motivated user wants to start again
      else if (action === "RETRY") {
        await this.initChat();
      }
      // Non-motivated user wants to get out here as soon as possible
      else if (action === "NEXT_EXERCISE") {
        nextExerciseRouter({
          assignmentId: this.assignmentId,
          courseUuid: this.courseUuid,
          ...this.nextExercise
        });
      }
    },

    showNextChatMessage() {
      chatHandler.moveToNextMessage({
        state: this.chat,
        audioDecoderPlayer: this.audioDecoderPlayer,
        onMsgDisplayStarted: async newState => {
          this.chat = newState;
          this.updateFooter();

          const lastItem = this.chat.display.lastItem;
          // If the user has a single-word text or all-fixed tiles text, it will
          // played as a CHAT_MESSAGE so only the last bot message has to be tracked
          // Also skip message without uuid such as "Alright let's just move on for now"
          if (lastItem.type === "CHAT_MESSAGE" && lastItem.side === "left" && lastItem.uuid) {
            this.onNewDictationEvent(DICTATION_EVENT.BOT_AUDIO_AUTOPLAY, {
              objectUuid: lastItem.uuid
            });
          } else if (lastItem.type === "CHAT_DICTATION_USER_SPEAKER") {
            this.onNewDictationEvent(DICTATION_EVENT.USER_AUDIO_AUTOPLAY, {});
          }
        },
        onMsgDisplayEnded: async newState => {
          this.chat = newState;

          if (
            this.chat.status === "IntroWaitForUser" ||
            this.chat.status === "ShowTrophy" ||
            this.chat.status === "Ended"
          ) {
            // do nothing
          } else if (newState.display.lastItem.type === "DICTATION_PROBLEM") {
            // load dictation problem
            const dictationReadyState = dictationProblemsHandler.loadNextProblem(
              this.dictationProblems,
              this.chat
            );
            this.chat = dictationReadyState.chat;
            this.dictationProblems = dictationReadyState.dictation;

            // Save the last event to record the bot message auto-play
            this.dictationEvent = this.dictationEvent.slice(this.dictationEvent.length - 1);

            this.showNextChatMessage();
          } else if (this.chat.status === "ProblemWaitingForUserAnswer") {
            this.exercise.footer.disabled = false;
            this.chat.allowUserInteraction = true;

            const chatBody = (this.$refs as any)["chat-body"];
            if (chatBody) {
              (chatBody as any).scrollBodyDown();
            }
            // do nothing
          } else {
            this.showNextChatMessage();
          }
        }
      });
      this.updateFooter();
    },

    onDictationAudioReplay(item) {
      if (item.type === "CHAT_MESSAGE") {
        this.onNewDictationEvent(DICTATION_EVENT.BOT_AUDIO_CLICK_TO_PLAY, {
          objectUuid: item.uuid
        });
      } else if (item.type === "CHAT_DICTATION_USER_SPEAKER") {
        this.onNewDictationEvent(DICTATION_EVENT.USER_AUDIO_CLICK_TO_PLAY, {});
      }
    },

    async onDictationSubmit(answer: DictationTile[]) {
      this.chat.allowUserInteraction = false;
      this.exercise.footer.disabled = true;

      this.onNewDictationEvent(DICTATION_EVENT.ANSWER_SUBMITTED, {});

      const dictationRespState = await dictationProblemsHandler.onUserAnswerSent(
        this,
        this.dictationProblems,
        this.chat,
        this.exercise,
        answer,
        this.dictationEvent
      );
      this.chat = dictationRespState.chat;
      this.dictationProblems = dictationRespState.dictation;
      this.dictationEvent = [];

      this.showNextChatMessage();
    },

    onDictationTileSelect(tile: DictationTile) {
      this.onNewDictationEvent(DICTATION_EVENT.TILE_CLICK_OPTIONS, { tokenId: tile.originalIndex });
    },

    onDictationTileUnselect(tile: DictationTile) {
      this.onNewDictationEvent(DICTATION_EVENT.TILE_CLICK_DISPLAY, { tokenId: tile.originalIndex });
    },

    onDictionaryClick() {
      window.alert("onDictionaryClick is TODO");
    },

    onImageOpened(item) {
      this.onNewDictationEvent(DICTATION_EVENT.IMAGE_OPENED, { objectUuid: item.uuid });
    },

    onImageClosed(item) {
      this.onNewDictationEvent(DICTATION_EVENT.IMAGE_CLOSED, { objectUuid: item.uuid });
    },

    onNewDictationEvent(type, args) {
      const event: DictationEvent = {
        eventType: type,
        timestamp: new Date().toISOString(),
        objectUuid: ""
      };

      switch (type) {
        case DICTATION_EVENT.BOT_AUDIO_AUTOPLAY:
        case DICTATION_EVENT.BOT_AUDIO_CLICK_TO_PLAY:
        case DICTATION_EVENT.IMAGE_OPENED:
        case DICTATION_EVENT.IMAGE_CLOSED:
          if (!args.objectUuid) {
            return;
          }
          event.objectUuid = args.objectUuid;
          break;

        case DICTATION_EVENT.USER_AUDIO_AUTOPLAY:
        case DICTATION_EVENT.USER_AUDIO_CLICK_TO_PLAY:
        case DICTATION_EVENT.ANSWER_SUBMITTED:
          if (!this.dictationProblems.current) {
            return;
          }
          event.objectUuid = this.dictationProblems.current.problemUuid;
          event.sentenceIndex = this.dictationProblems.current.sentenceIndex;
          break;

        case DICTATION_EVENT.TILE_CLICK_DISPLAY:
        case DICTATION_EVENT.TILE_CLICK_OPTIONS:
          if (!this.dictationProblems.current) {
            return;
          }
          event.objectUuid = this.dictationProblems.current.problemUuid;
          event.sentenceIndex = this.dictationProblems.current.sentenceIndex;
          event.tokenId = args.tokenId;
          break;
      }

      this.dictationEvent = [...this.dictationEvent, event];
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },

    updateFooter() {
      this.exercise = exerciseHandler.loadFooterFromChat(this, this.exercise, this.chat);
    }
  }
});
